import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion, type PanInfo } from 'framer-motion'
import { PressScale } from '../../lib/PressScale'
import { IconClose } from '../../mobile/components/Icons'
import { useIsAppMode } from '../../mobile/lib/appMode'

/**
 * OrgDialog — one modal host for both shells.
 *
 * App mode anchors to the bottom edge with a grab handle and drag-down
 * dismissal (the `Sheet` idiom); desktop centers a portal panel in the
 * `ShareDialog` idiom. One component rather than two so the CONTENT is written
 * once and the two shells cannot drift apart in what they say.
 *
 * ── IT HAS FOUR EXITS, AND THAT IS THE POINT ──────────────────────────────
 * A visible close control, the scrim, Escape, and (app mode) drag-down. The
 * last review round found a shipped focus-trapping overlay whose only control
 * signed the user out; every dialog in this family renders a close affordance
 * even when the caller also supplies its own "Not now".
 *
 * Focus moves to the heading on open and RETURNS to whatever opened it on
 * close — losing focus to `<body>` is how a keyboard user gets stranded.
 * Tab is trapped inside the panel while it is open, which is what
 * `aria-modal="true"` promises.
 */
export function OrgDialog({
  open,
  onClose,
  title,
  children,
  testId,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  testId?: string
}) {
  const appMode = useIsAppMode()
  const reduce = useReducedMotion()
  const panelRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const openerRef = useRef<Element | null>(null)
  const headingId = useId()

  // Remember who opened it, take focus to the heading, hand focus back on
  // close. `preventScroll` so a long page doesn't jump behind the scrim.
  useEffect(() => {
    if (!open) return
    openerRef.current = document.activeElement
    const t = window.setTimeout(() => headingRef.current?.focus({ preventScroll: true }), 0)
    return () => {
      window.clearTimeout(t)
      const opener = openerRef.current
      if (opener instanceof HTMLElement && document.contains(opener)) {
        opener.focus({ preventScroll: true })
      }
    }
  }, [open])

  // Escape closes; Tab cycles inside the panel.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return }
      if (e.key !== 'Tab') return
      const panel = panelRef.current
      if (!panel) return
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      if (e.shiftKey && (active === first || active === headingRef.current)) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Lock the page behind the scrim.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  if (typeof document === 'undefined') return null

  function onDragEnd(_: unknown, info: PanInfo) {
    if (info.offset.y > 90 || info.velocity.y > 600) onClose()
  }

  const panel = (
    <motion.div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      initial={reduce ? { opacity: 0 } : appMode ? { y: '100%' } : { opacity: 0, y: 12, scale: 0.985 }}
      animate={reduce ? { opacity: 1 } : appMode ? { y: 0 } : { opacity: 1, y: 0, scale: 1 }}
      exit={reduce ? { opacity: 0 } : appMode ? { y: '100%' } : { opacity: 0, y: 8, scale: 0.99 }}
      transition={reduce ? { duration: 0.15 } : { type: 'spring', stiffness: 340, damping: 34 }}
      {...(appMode && !reduce
        ? {
            drag: 'y' as const,
            dragConstraints: { top: 0, bottom: 0 },
            dragElastic: { top: 0, bottom: 0.6 },
            onDragEnd,
          }
        : {})}
      className={`lqd-glass relative flex max-h-[88dvh] w-full flex-col overflow-hidden bg-paper/95 shadow-lift ${
        appMode ? 'rounded-t-[24px]' : 'max-w-[520px] rounded-lg'
      }`}
      data-testid={testId}
    >
      {appMode && (
        <div className="flex shrink-0 justify-center pb-1 pt-2.5" aria-hidden="true">
          <div className="h-1 w-9 rounded-full bg-navy/15" />
        </div>
      )}
      <div className="flex shrink-0 items-start justify-between gap-3 px-5 pb-2 pt-3">
        <h2
          ref={headingRef}
          id={headingId}
          tabIndex={-1}
          className="font-serif text-[20px] font-bold leading-snug text-ink outline-none"
        >
          {title}
        </h2>
        <PressScale
          feel="tap"
          onClick={onClose}
          className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-soft"
          aria-label="Close"
          data-testid={testId ? `${testId}-close` : undefined}
        >
          <IconClose size={20} />
        </PressScale>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-6 pt-1"
        style={appMode ? { paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' } : undefined}
      >
        {children}
      </div>
    </motion.div>
  )

  return createPortal(
    <AnimatePresence>
      {open && (
        <div
          className={`fixed inset-0 z-[60] mx-auto flex w-full ${
            appMode ? 'max-w-[480px] flex-col justify-end' : 'items-center justify-center p-4'
          }`}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-navy/40"
            onClick={onClose}
            aria-hidden="true"
          />
          {panel}
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
